/**
 * TopNavbar — BitDrop floating navigation bar.
 * 
 * Design spec:
 * - Position: fixed, top: 16px, right: 16px/24px, z-index: 10
 * - Border Radius: 12px (pill corners)
 * - Height: 48px
 * - Zero shadows (clean 1px border)
 * - Monochrome light/dark theme support with crisp SVG stroke icons
 */

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, Pressable } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { Colors, Radius, Spacing, Layout } from '../theme/tokens';
import { Icon } from '../atoms/Icon';
import { SITE_NAV_LINKS } from '../constants/navigation';

export interface TopNavbarProps {
  statusText?: string;
  onNavigateToSettings?: () => void;
  offsetSidebar?: boolean;
}

export const TopNavbar: React.FC<TopNavbarProps> = ({
  statusText = 'P2P Swarm Ready',
  onNavigateToSettings,
  offsetSidebar = true,
}) => {
  const { colors, isDark } = useTheme();
  const { isDesktop } = useBreakpoint();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [hoveredDesktopLink, setHoveredDesktopLink] = useState<string | null>(null);

  const containerLeft = (isDesktop && offsetSidebar) ? Layout.sidebarWidth + Spacing.lg : Spacing.lg;

  return (
    <>
      <View style={[styles.topNavbarContainer, { left: containerLeft, top: Spacing.lg, right: Spacing.lg }]}>
        {/* Top-Left: BitDrop Logo & Title (Tablet & Mobile only, inverted against wallpaper) */}
        {!isDesktop ? (
          <TouchableOpacity
            style={styles.logoGroup}
            activeOpacity={0.8}
            onPress={() => {
              if (typeof window !== 'undefined') window.location.href = '/';
            }}
          >
            <View style={styles.logoBadge}>
              <Icon name="logo" size={28} color={isDark ? colors.text : Colors.white} />
            </View>
            <Text style={[styles.logoName, { color: isDark ? colors.text : Colors.white }]}>BitDrop</Text>
          </TouchableOpacity>
        ) : (
          <View />
        )}

        {/* Top-Right: Navigation & Actions */}
        <View style={styles.rightActions}>
          {/* Desktop Nav Links Pill (>= 1024px) */}
          {isDesktop && (
            <View
              style={[
                styles.pill,
                {
                  backgroundColor: colors.surface,
                  borderColor: colors.border,
                },
              ]}
            >
              {SITE_NAV_LINKS.map(({ label, href }) => {
                const isHovered = hoveredDesktopLink === label;
                return (
                  <Pressable
                    key={label}
                    style={styles.navLink}
                    onHoverIn={() => setHoveredDesktopLink(label)}
                    onHoverOut={() => setHoveredDesktopLink(null)}
                    onPress={() => {
                      if (typeof window !== 'undefined') {
                        window.location.href = href;
                      }
                    }}
                  >
                    <Text
                      style={[
                        styles.navLinkText,
                        { color: isHovered ? colors.text : colors.subtext },
                      ]}
                    >
                      {label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          )}

          {/* P2P Swarm Status Pill */}
          <TouchableOpacity
            style={[
              styles.pill,
              styles.statusPill,
              {
                backgroundColor: colors.surface,
                borderColor: colors.border,
              },
            ]}
            onPress={onNavigateToSettings}
            activeOpacity={0.8}
          >
            <View style={styles.statusDot} />
            <Text style={[styles.statusText, { color: colors.text }]}>
              {statusText}
            </Text>
          </TouchableOpacity>

          {/* Mobile & Tablet Menu Button (< 1024px) */}
          {!isDesktop && (
            <Pressable
              style={[
                styles.iconPill,
                {
                  backgroundColor: colors.surface,
                  borderColor: colors.border,
                },
              ]}
              onPress={() => setIsDrawerOpen(true)}
            >
              <Icon name="menu" size={20} color={colors.text} />
            </Pressable>
          )}
        </View>
      </View>

      {/* Mobile Drawer Menu Modal */}
      <Modal visible={isDrawerOpen} transparent animationType="fade">
        <View style={styles.drawerOverlay}>
          <TouchableOpacity
            style={[
              styles.drawerBackdrop,
              { backgroundColor: isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.45)' },
            ]}
            activeOpacity={1}
            onPress={() => setIsDrawerOpen(false)}
          />
          <View
            style={[
              styles.drawerContent,
              {
                backgroundColor: colors.surface,
                borderLeftColor: colors.border,
              },
            ]}
          >
            {/* Drawer Header */}
            <View style={styles.drawerHeader}>
              <TouchableOpacity
                style={styles.logoGroup}
                activeOpacity={0.8}
                onPress={() => {
                  setIsDrawerOpen(false);
                  if (typeof window !== 'undefined') window.location.href = '/';
                }}
              >
                <View style={styles.logoBadge}>
                  <Icon name="logo" size={28} color={colors.text} />
                </View>
                <Text style={[styles.logoName, { color: colors.text }]}>BitDrop</Text>
              </TouchableOpacity>

              <Pressable
                style={[
                  styles.drawerCloseBtn,
                  {
                    backgroundColor: 'transparent',
                    borderColor: colors.border,
                  },
                ]}
                onPress={() => setIsDrawerOpen(false)}
              >
                <Icon name="close" size={18} color={colors.text} />
              </Pressable>
            </View>

            {/* Status Pill Inside Drawer */}
            <TouchableOpacity
              style={[
                styles.drawerStatusBanner,
                {
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                  borderColor: colors.border,
                },
              ]}
              onPress={() => {
                setIsDrawerOpen(false);
                onNavigateToSettings?.();
              }}
              activeOpacity={0.8}
            >
              <View style={styles.statusDot} />
              <Text style={[styles.drawerStatusText, { color: colors.text }]}>
                {statusText}
              </Text>
              <Icon name="chevron-right" size={14} color={colors.subtext} />
            </TouchableOpacity>

            {/* Drawer Nav Links */}
            <View style={styles.drawerNavList}>
              {SITE_NAV_LINKS.map(({ label, href }) => {
                const isHovered = hoveredLink === label;
                return (
                  <Pressable
                    key={label}
                    style={[
                      styles.drawerNavItem,
                      {
                        backgroundColor: 'transparent',
                        borderColor: colors.border,
                      },
                    ]}
                    onHoverIn={() => setHoveredLink(label)}
                    onHoverOut={() => setHoveredLink(null)}
                    onPress={() => {
                      setIsDrawerOpen(false);
                      if (typeof window !== 'undefined') {
                        window.location.href = href;
                      }
                    }}
                  >
                    <Text style={[styles.drawerNavText, { color: isHovered ? colors.text : colors.subtext }]}>
                      {label}
                    </Text>
                    <Icon name="chevron-right" size={14} color={colors.subtext} />
                  </Pressable>
                );
              })}
            </View>

            {/* Drawer CTA Action */}
            <Pressable
              style={({ hovered }: any) => [
                styles.drawerCtaItem,
                {
                  backgroundColor: colors.brand,
                  opacity: hovered ? 0.9 : 1,
                },
              ]}
              onPress={() => {
                setIsDrawerOpen(false);
                if (typeof window !== 'undefined') {
                  window.location.href = '/';
                }
              }}
            >
              <Text style={[styles.drawerCtaText, { color: colors.brandText }]}>
                Open App →
              </Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </>
  );
};

// ─── Styles — BitDrop pill nav design tokens ────────────────────────────────────

const styles = StyleSheet.create({
  topNavbarContainer: {
    position: 'fixed' as any,
    zIndex: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    pointerEvents: 'box-none' as any,
  } as any,

  logoGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    pointerEvents: 'auto' as any,
  },

  logoBadge: {
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },

  logoName: {
    fontSize: 20,
    fontWeight: '800',
    letterSpacing: -0.6,
  },

  rightActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    pointerEvents: 'auto' as any,
  } as any,

  // Pill style — zero shadows, 1px border
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 44,
    borderRadius: Radius.lg, // 12px
    borderWidth: 1,
    paddingHorizontal: Spacing.xs,
  },

  iconPill: {
    width: 44,
    height: 44,
    borderRadius: Radius.lg, // 12px
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 0,
  },

  statusPill: {
    paddingHorizontal: Spacing.md,
    gap: Spacing.sm,
  },

  navLink: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 36,
    paddingHorizontal: Spacing.sm,
    borderRadius: Radius.md,
  },

  navLinkText: {
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: -0.2,
  },

  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#10B981',
  },

  statusText: {
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: -0.2,
  },

  // Drawer Styles — zero shadows, clean 1px border
  drawerOverlay: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  drawerBackdrop: {
    ...StyleSheet.absoluteFillObject,
    // @ts-ignore
    backdropFilter: 'blur(8px)',
  },
  drawerContent: {
    width: 290,
    height: '100%',
    padding: Spacing.lg,
    borderLeftWidth: 1,
    flexDirection: 'column',
  },
  drawerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Spacing.lg,
  },
  drawerCloseBtn: {
    width: 36,
    height: 36,
    borderRadius: Radius.md,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
  },
  drawerStatusBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm + 2,
    borderRadius: Radius.md,
    borderWidth: 1,
    marginBottom: Spacing.md,
  },
  drawerStatusText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    marginLeft: Spacing.sm,
    letterSpacing: -0.2,
  },
  drawerNavList: {
    flex: 1,
    flexDirection: 'column',
    gap: Spacing.xs,
  },
  drawerNavItem: {
    height: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.md,
    borderWidth: 1,
  },
  drawerNavText: {
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: -0.2,
  },
  drawerCtaItem: {
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    borderRadius: Radius.md,
    marginTop: 'auto',
  },
  drawerCtaText: {
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: -0.2,
  },
});
